import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ScoreGauge from './ScoreGauge';

const riskColors = { low: '#10b981', medium: '#f59e0b', high: '#ef4444' };

function ScreeningReport() {
  const { id } = useParams();
  const [screening, setScreening] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/screening/${id}/report`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Failed to load report');
        setScreening(data.screening);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [id]);

  if (loading) return <main className="App-main"><p style={{ textAlign: 'center', marginTop: '3rem' }}>Loading report...</p></main>;
  if (error) return <main className="App-main"><p className="error-message" style={{ textAlign: 'center', marginTop: '3rem' }}>{error}</p></main>;
  if (!screening) return null;

  const td = screening.tenant_data || {};
  const cr = screening.credit_report || {};
  const ai = screening.ai_assessment || {};
  const riskLevel = (ai.risk_level || 'unknown').toLowerCase();
  const riskColor = riskColors[riskLevel] || '#6b7280';

  // Normalize credit report structure (handles both TU normalized and legacy flat)
  const scoreValue = cr.score?.value || cr.score || cr.creditScore || 0;
  const scoreModel = cr.score?.model || '';
  const scoreReasons = cr.score?.reasonCodes || [];
  const summary = cr.summary || {};
  const tradelines = cr.tradelines || cr.trades || [];
  const collections = cr.collections || { count: 0, items: [] };
  const collectionCount = collections.count ?? collections.length ?? 0;
  const collectionItems = collections.items || (Array.isArray(collections) ? collections : []);
  const publicRecords = cr.publicRecords || {};
  const inquiries = cr.inquiries || [];
  const transAlerts = cr.transAlerts || [];

  const sectionStyle = {
    padding: '1.25rem 1.5rem', borderRadius: '12px',
    border: '1px solid var(--border-color, #e5e7eb)', marginBottom: '1rem'
  };
  const labelStyle = { fontSize: '0.78rem', color: 'var(--light-text-color)', fontWeight: 500, marginBottom: '0.15rem' };
  const valueStyle = { fontSize: '0.95rem', fontWeight: 600 };

  const rtiPct = screening.rent_amount && td.monthly_income
    ? Math.round((Number(screening.rent_amount) / Number(td.monthly_income)) * 100)
    : null;

  return (
    <main className="App-main">
      <div style={{ maxWidth: 680, margin: '2rem auto' }}>
        <Link to="/landlord/screenings" style={{ fontSize: '0.85rem', color: 'var(--accent-color)', display: 'inline-block', marginBottom: '1rem' }}>← All screenings</Link>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h2 style={{ margin: '0 0 0.25rem' }}>Screening Report</h2>
          <p className="status-text">
            {td.full_name || screening.tenant_name || screening.tenant_email}
            {' · '}
            {screening.rental_address}{screening.rental_unit ? `, Unit ${screening.rental_unit}` : ''}
          </p>
          {screening.report_generated_at && (
            <p className="status-text" style={{ fontSize: '0.78rem' }}>
              Generated {new Date(screening.report_generated_at).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* ── KPI Gauges ── */}
        <div style={{
          ...sectionStyle, display: 'flex', justifyContent: 'center', alignItems: 'flex-start',
          gap: '1.5rem', flexWrap: 'wrap', padding: '1.5rem'
        }}>
          {scoreValue > 0 && (
            <ScoreGauge
              value={scoreValue}
              minValue={300}
              maxValue={900}
              label="Credit Score"
              sublabel={scoreModel || undefined}
              rating={scoreValue >= 700 ? 'Good' : scoreValue >= 600 ? 'Fair' : 'Needs Review'}
              ratingColor={scoreValue >= 700 ? '#16A34A' : scoreValue >= 600 ? '#f59e0b' : '#ef4444'}
              size={130}
            />
          )}
          {rtiPct !== null && (
            <ScoreGauge
              value={rtiPct}
              minValue={0}
              maxValue={100}
              label="Rent-to-Income"
              sublabel={`$${Number(screening.rent_amount).toLocaleString()} / $${Number(td.monthly_income).toLocaleString()}`}
              rating={rtiPct <= 30 ? 'Healthy' : rtiPct <= 40 ? 'Moderate' : 'High'}
              ratingColor={rtiPct <= 30 ? '#16A34A' : rtiPct <= 40 ? '#f59e0b' : '#ef4444'}
              size={130}
              invertColors={true}
            />
          )}
          {summary.utilizationPct != null && (
            <ScoreGauge
              value={summary.utilizationPct}
              minValue={0}
              maxValue={100}
              label="Credit Utilization"
              rating={summary.utilizationPct <= 30 ? 'Low' : summary.utilizationPct <= 50 ? 'Moderate' : summary.utilizationPct <= 75 ? 'High' : 'Very High'}
              ratingColor={summary.utilizationPct <= 30 ? '#16A34A' : summary.utilizationPct <= 50 ? '#f59e0b' : '#ef4444'}
              size={130}
              invertColors={true}
            />
          )}
          {ai.risk_level && (
            <ScoreGauge
              value={riskLevel === 'low' ? 20 : riskLevel === 'medium' ? 55 : 85}
              minValue={0}
              maxValue={100}
              label="Risk Level"
              rating={riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)}
              ratingColor={riskColor}
              size={130}
              invertColors={true}
            />
          )}
        </div>

        {/* ── AI Summary ── */}
        {ai.summary && (
          <div style={sectionStyle}>
            <p style={{ ...labelStyle, marginBottom: '0.5rem' }}>AI Assessment</p>
            <p style={{ fontSize: '0.92rem', lineHeight: 1.6, margin: 0 }}>{ai.summary}</p>
            {ai.recommendation && (
              <div style={{ marginTop: '0.75rem', padding: '0.6rem 0.85rem', borderRadius: '8px', background: 'rgba(40,182,200,0.06)', border: '1px solid rgba(40,182,200,0.15)' }}>
                <p style={{ margin: 0, fontSize: '0.85rem', lineHeight: 1.5 }}>
                  <strong style={{ color: 'var(--accent-color)' }}>Recommendation:</strong> {ai.recommendation}
                </p>
              </div>
            )}
            {ai.income_note && (
              <p className="status-text" style={{ marginTop: '0.75rem', fontSize: '0.82rem', fontStyle: 'italic' }}>
                {ai.income_note}
              </p>
            )}
          </div>
        )}

        {/* ── Credit Profile ── */}
        <div style={sectionStyle}>
          <p style={{ ...labelStyle, marginBottom: '0.75rem' }}>Credit Profile (TransUnion)</p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '1rem' }}>
            <div>
              <p style={labelStyle}>Tradelines</p>
              <p style={valueStyle}>
                {summary.totalTradelines || tradelines.length || '—'}
                {summary.openTradelines != null && <span style={{ fontSize: '0.75rem', fontWeight: 400, color: 'var(--light-text-color)' }}> ({summary.openTradelines} open)</span>}
              </p>
            </div>
            <div>
              <p style={labelStyle}>Collections</p>
              <p style={{ ...valueStyle, color: collectionCount > 0 ? '#ef4444' : 'inherit' }}>
                {collectionCount > 0 ? collectionCount : 'None'}
              </p>
            </div>
            <div>
              <p style={labelStyle}>Bankruptcies</p>
              <p style={{ ...valueStyle, color: (publicRecords.bankruptcies || 0) > 0 ? '#ef4444' : 'inherit' }}>
                {publicRecords.bankruptcies > 0 ? publicRecords.bankruptcies : 'None'}
              </p>
            </div>
            <div>
              <p style={labelStyle}>Judgments / Liens</p>
              <p style={{ ...valueStyle, color: ((publicRecords.judgments || 0) + (publicRecords.liens || 0)) > 0 ? '#ef4444' : 'inherit' }}>
                {(publicRecords.judgments || 0) + (publicRecords.liens || 0) > 0
                  ? `${publicRecords.judgments || 0} / ${publicRecords.liens || 0}`
                  : 'None'}
              </p>
            </div>
            <div>
              <p style={labelStyle}>Late Payments</p>
              <p style={{ ...valueStyle, color: (summary.totalLatePayments || 0) > 0 ? '#f59e0b' : 'inherit' }}>
                {summary.totalLatePayments != null ? summary.totalLatePayments : '—'}
              </p>
            </div>
            <div>
              <p style={labelStyle}>Account Age</p>
              <p style={valueStyle}>{summary.oldestAccountYears ? `${summary.oldestAccountYears} yr` : '—'}</p>
            </div>
            {summary.totalDebt != null && (
              <div>
                <p style={labelStyle}>Total Debt</p>
                <p style={valueStyle}>${Number(summary.totalDebt).toLocaleString()}</p>
              </div>
            )}
            {summary.totalMonthlyDebt != null && (
              <div>
                <p style={labelStyle}>Monthly Debt</p>
                <p style={valueStyle}>${Number(summary.totalMonthlyDebt).toLocaleString()}</p>
              </div>
            )}
            {inquiries.length > 0 && (
              <div>
                <p style={labelStyle}>Recent Inquiries</p>
                <p style={valueStyle}>{inquiries.length}</p>
              </div>
            )}
          </div>

          {/* Score reason codes */}
          {scoreReasons.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <p style={labelStyle}>Score Factor Codes</p>
              <div style={{ display: 'flex', gap: '0.35rem', flexWrap: 'wrap', marginTop: '0.25rem' }}>
                {scoreReasons.map((code, i) => (
                  <span key={i} style={{ fontSize: '0.75rem', fontWeight: 600, padding: '0.15rem 0.5rem', borderRadius: '999px', background: 'var(--secondary-color, #f4f6fb)', border: '1px solid var(--border-color)' }}>
                    {code}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* AI credit highlights */}
          {ai.credit_highlights && ai.credit_highlights.length > 0 && (
            <div style={{ marginTop: '1rem' }}>
              <p style={labelStyle}>Key Findings</p>
              <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.25rem' }}>
                {ai.credit_highlights.map((h, i) => (
                  <li key={i} style={{ fontSize: '0.88rem', lineHeight: 1.5 }}>{h}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* ── Collection Details ── */}
        {collectionItems.length > 0 && (
          <div style={{ ...sectionStyle, borderColor: '#ef444430', background: 'rgba(239,68,68,0.03)' }}>
            <p style={{ ...labelStyle, color: '#ef4444', marginBottom: '0.5rem' }}>Collection Details</p>
            {collectionItems.map((c, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.4rem 0', borderBottom: i < collectionItems.length - 1 ? '1px solid var(--border-color)' : 'none' }}>
                <span style={{ fontSize: '0.88rem' }}>{c.creditor || 'Unknown creditor'}</span>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#ef4444' }}>${Number(c.currentBalance || c.amount || 0).toLocaleString()}</span>
                  {c.status && <span className="status-text" style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }}>{c.status}</span>}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Fraud Alerts ── */}
        {transAlerts.length > 0 && (
          <div style={{ ...sectionStyle, borderColor: '#ef444430', background: 'rgba(239,68,68,0.03)' }}>
            <p style={{ ...labelStyle, color: '#ef4444', marginBottom: '0.35rem' }}>⚠️ Fraud Alerts</p>
            {transAlerts.map((a, i) => (
              <p key={i} style={{ fontSize: '0.88rem', margin: '0.25rem 0' }}>{a.text || a.code}</p>
            ))}
          </div>
        )}

        {/* ── Income & Employment ── */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
            <p style={{ ...labelStyle, marginBottom: 0 }}>Income & Employment</p>
            <span style={{ fontSize: '0.7rem', background: '#f59e0b20', color: '#f59e0b', padding: '0.15rem 0.4rem', borderRadius: '999px', fontWeight: 600 }}>Self-reported</span>
            {ai.employment_match && ai.employment_match !== 'no_bureau_data' && (
              <span style={{
                fontSize: '0.7rem', fontWeight: 600, padding: '0.15rem 0.4rem', borderRadius: '999px',
                background: ai.employment_match === 'match' ? '#10b98120' : '#ef444420',
                color: ai.employment_match === 'match' ? '#10b981' : '#ef4444'
              }}>
                {ai.employment_match === 'match' ? '✓ Bureau match' : '⚠ Bureau mismatch'}
              </span>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            <div>
              <p style={labelStyle}>Employer</p>
              <p style={valueStyle}>{td.employer || '—'}</p>
            </div>
            <div>
              <p style={labelStyle}>Job Title</p>
              <p style={valueStyle}>{td.job_title || '—'}</p>
            </div>
            <div>
              <p style={labelStyle}>Monthly Income</p>
              <p style={valueStyle}>{td.monthly_income ? `$${Number(td.monthly_income).toLocaleString()}` : '—'}</p>
            </div>
            <div>
              <p style={labelStyle}>Years Employed</p>
              <p style={valueStyle}>{td.years_employed || '—'}</p>
            </div>
          </div>
        </div>

        {/* ── AI Ratings ── */}
        {(ai.credit_utilization_rating || ai.payment_history_rating) && (
          <div style={sectionStyle}>
            <p style={{ ...labelStyle, marginBottom: '0.75rem' }}>AI Ratings</p>
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              {ai.payment_history_rating && (
                <div style={{ flex: '1 1 200px', padding: '0.75rem', borderRadius: '10px', background: 'var(--secondary-color, #f4f6fb)', textAlign: 'center' }}>
                  <p style={labelStyle}>Payment History</p>
                  <p style={{
                    fontSize: '1rem', fontWeight: 700, margin: '0.25rem 0 0', textTransform: 'capitalize',
                    color: ['excellent', 'good'].includes(ai.payment_history_rating) ? '#16A34A' : ai.payment_history_rating === 'fair' ? '#f59e0b' : '#ef4444'
                  }}>
                    {ai.payment_history_rating}
                  </p>
                </div>
              )}
              {ai.credit_utilization_rating && (
                <div style={{ flex: '1 1 200px', padding: '0.75rem', borderRadius: '10px', background: 'var(--secondary-color, #f4f6fb)', textAlign: 'center' }}>
                  <p style={labelStyle}>Credit Utilization</p>
                  <p style={{
                    fontSize: '1rem', fontWeight: 700, margin: '0.25rem 0 0', textTransform: 'capitalize',
                    color: ai.credit_utilization_rating === 'low' ? '#16A34A' : ai.credit_utilization_rating === 'moderate' ? '#f59e0b' : '#ef4444'
                  }}>
                    {ai.credit_utilization_rating}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Concerns ── */}
        {ai.concerns && ai.concerns.length > 0 && (
          <div style={{ ...sectionStyle, borderColor: '#ef444430', background: 'rgba(239,68,68,0.03)' }}>
            <p style={{ ...labelStyle, color: '#ef4444' }}>Concerns</p>
            <ul style={{ margin: '0.35rem 0 0', paddingLeft: '1.25rem' }}>
              {ai.concerns.map((c, i) => (
                <li key={i} style={{ fontSize: '0.88rem', lineHeight: 1.5 }}>{c}</li>
              ))}
            </ul>
          </div>
        )}

        {/* ── Previous Landlord ── */}
        {(td.previous_landlord_name || td.previous_landlord_phone) && (
          <div style={sectionStyle}>
            <p style={{ ...labelStyle, marginBottom: '0.5rem' }}>Previous Landlord Reference</p>
            {td.previous_landlord_name && <p style={{ fontSize: '0.9rem', margin: '0 0 0.15rem' }}>{td.previous_landlord_name}</p>}
            {td.previous_landlord_phone && <p className="status-text" style={{ fontSize: '0.85rem', margin: 0 }}>{td.previous_landlord_phone}</p>}
          </div>
        )}

        {/* ── Upsell ── */}
        <div style={{
          ...sectionStyle, textAlign: 'center', marginTop: '2rem',
          background: 'var(--surface-color, var(--secondary-color, #f9fafb))'
        }}>
          <p style={{ fontWeight: 700, fontSize: '1rem', margin: '0 0 0.35rem' }}>Want more confidence?</p>
          <p className="status-text" style={{ fontSize: '0.85rem', lineHeight: 1.55, marginBottom: '1rem' }}>
            With Pro, tenants upload paystubs and documents. Get verified income, fraud detection, and auto-generated leases.
          </p>
          <Link to="/pricing" className="secondary-button" style={{ textDecoration: 'none', display: 'inline-block', fontSize: '0.85rem' }}>
            Explore Pro — $20/mo
          </Link>
        </div>
      </div>
    </main>
  );
}

export default ScreeningReport;
